/**
 * Entropy calculation types
 * 
 * Aligned with backend entropy calculation from correction_factor
 */

export interface CorrectionFactor {
  ideal: number;
  actual: number;
  correction_factor: number; // ideal / actual
}

export interface EntropyCalculation {
  artifact_item_id: string;
  correction_factor: number;
  state: string; // ACTIVE, DRAFTED, CREATED, etc.
  entropy_score: number; // 1-10
  calculation_method: 'formula' | 'state_override';
  ideal_value?: number;
  actual_value?: number;
}

export interface EntropyScoreInterpretation {
  score: number;
  label: string;
  description: string;
  improvement_potential: 'low' | 'moderate' | 'high' | 'very_high' | 'critical';
}

export const ENTROPY_INTERPRETATIONS: Record<number, EntropyScoreInterpretation> = {
  1: { score: 1, label: 'Optimal', description: 'Well-optimized, minimal improvement potential', improvement_potential: 'low' },
  2: { score: 2, label: 'Good', description: 'Well-optimized, minimal improvement potential', improvement_potential: 'low' },
  3: { score: 3, label: 'Moderate', description: 'Some improvement potential', improvement_potential: 'moderate' },
  4: { score: 4, label: 'Moderate', description: 'Some improvement potential', improvement_potential: 'moderate' },
  5: { score: 5, label: 'High', description: 'Significant improvement potential', improvement_potential: 'high' },
  6: { score: 6, label: 'High', description: 'Significant improvement potential', improvement_potential: 'high' },
  7: { score: 7, label: 'Very High', description: 'Major improvement needed', improvement_potential: 'very_high' },
  8: { score: 8, label: 'ACTIVE (No Actuals)', description: 'ACTIVE state with no actual values', improvement_potential: 'critical' },
  9: { score: 9, label: 'DRAFTED', description: 'DRAFTED state (not yet operational)', improvement_potential: 'critical' },
  10: { score: 10, label: 'CREATED/Error', description: 'CREATED state or error condition (not operational)', improvement_potential: 'critical' },
};

/** API may return non-integer scores (e.g. 5.47); keys in ENTROPY_INTERPRETATIONS are 1–10 integers. */
export function getEntropyInterpretation(rawScore: number): EntropyScoreInterpretation {
  const n = Number(rawScore);
  if (Number.isNaN(n)) {
    return ENTROPY_INTERPRETATIONS[5];
  }
  const key = Math.min(10, Math.max(1, Math.round(n)));
  return ENTROPY_INTERPRETATIONS[key] ?? ENTROPY_INTERPRETATIONS[5];
}
